import {
	fetchJiraPRsQuery,
	parseJiraPRResponse,
} from '@atlassian/search-client/hydration/pr-fetch';
import type { IssueLinkedPRs } from '@atlassian/search-client/hydration/pr-types';

import type { FireAnalyticsEvent } from '../../../common/types';
import {
	onLinkedPRsFetchFail,
	onLinkedPRsFetchSuccess,
} from '../../../common/utils/events/linked-prs-fetch';

export const fetchAndUpdateJiraLinkedPRs = async (
	issueAris: string[],
	cloudId: string | undefined,
	aggAbsoluteUrl: string | undefined,
	setLinkedPRs: React.Dispatch<React.SetStateAction<IssueLinkedPRs>>,
	logError: (error: unknown) => void,
	fireAnalyticsEvent?: (payload: FireAnalyticsEvent) => void,
): Promise<void> => {
	if (!cloudId || issueAris.length === 0) {
		return;
	}

	const startTime = performance.now();

	try {
		const response = await fetchJiraPRsQuery({
			issueAris,
			aggAbsoluteUrl,
			cloudId,
		});

		const parsed = parseJiraPRResponse(response, issueAris);
		const latencyMs = Math.round(performance.now() - startTime);

		const prCount = Object.values(parsed).reduce((total, entry) => {
			if (entry.status === 'loaded') {
				return total + entry.prs.length;
			}
			return total;
		}, 0);

		const issuesWithPRsCount = Object.values(parsed).filter(
			(entry) => entry.status === 'loaded' && entry.prs.length > 0,
		).length;

		fireAnalyticsEvent?.(
			onLinkedPRsFetchSuccess({
				issueCount: issueAris.length,
				prCount,
				issuesWithPRsCount,
				latencyMs,
			}),
		);

		setLinkedPRs((prev) => ({
			...prev,
			...parsed,
		}));
	} catch (e) {
		const latencyMs = Math.round(performance.now() - startTime);

		fireAnalyticsEvent?.(
			onLinkedPRsFetchFail({
				reason: e instanceof Error ? e.message : 'unknown',
				latencyMs,
			}),
		);

		logError(e);
		// On failure, set error entries so UI knows hydration attempted but failed
		setLinkedPRs((prev) => {
			const updated = { ...prev };
			for (const ari of issueAris) {
				updated[ari] = { status: 'error' };
			}
			return updated;
		});
	}
};
